import CirclesPurpleIcon from '@src/assets/icons/circles-purple.svg'
import LogoIcon from '@src/assets/icons/logo-icon.svg'
import CtaBanner from '@src/components/CtaBanner'
import SubjectCard from '@src/components/SubjectCard'
import FeedbackHome from '@src/components/feedbackHome'
import PageSlugs from '@src/components/layout/pageSlugs'
import AdsBlocksSection from '@src/components/sections/adsBlocksSection/AdsBlocksSection'
import EducationExpertSection from '@src/components/sections/educationExpertSection/educationExpertSection';
import SubjectsDropdown from '@src/components/sections/homeBanner/components/subjectsDropdown';
import HomeBanner from '@src/components/sections/homeBanner/homeBanner';
import HowThisWorkSection from '@src/components/sections/howItWorksSection/howItWorksSection';
import { getHowItWorksSectionViewModel } from '@src/components/sections/howItWorksSection/howItWorksSectionMapper';
import PageBanner from '@src/components/sections/pageBanner/pageBanner';
import TeacherSection from '@src/components/teacherSection';
import SubjectsPresenter from '@src/subjects/subjectsPresenter';
import api from '@src/utils/api.js';
import { renderHtmlToReact } from '@src/utils/html';
import {
  convertToPercentage,
  currencyToSymbol,
  getSiteCurrency,
} from '@src/utils/pricing';
import { Themed } from '@theme-ui/mdx';
import { Link } from 'gatsby';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Flex, Grid } from 'theme-ui';

const subjectsPresenter = new SubjectsPresenter();

const loadData = async (controller) => {
  const response = {
    bestTeachers: null,
    feedback: null,
    teacherCounts: null,
  };

  const loadTeachers = api.get(
    'teachers?' +
      new URLSearchParams({ limit: 6, currency: getSiteCurrency() }),
    {
      controller,
    }
  );

  const loadFeedback = api.get(
    'ratings?' + new URLSearchParams({ limit: 20, minimumRating: 4 }),
    {
      controller,
    }
  );

  let teachers = [];
  let feedback = [];
  try {
    const [teachersRes, feedbackRes] = await Promise.all([
      loadTeachers,
      loadFeedback,
    ]);
    teachers = teachersRes.data.filter(
      (t) => t.available_slots && t.available_slots > 0
    );
    feedback = feedbackRes;
  } catch (error) {
    console.log(error);
  }

  response.bestTeachers = teachers?.map((t) => {
    const description = t.description || '';
    const lessonPrice = parseFloat(t.lessonprice).toFixed(2);

    return {
      id: t.userId,
      slug: t.tag,
      avatarUrl: t.avatarUrl,
      firstName: t.firstName,
      description:
        description.length > 220
          ? description.slice(0, 220) + '...'
          : description,
      videoUrl: t.videoUrl,
      // subject: teacher.subject,
      price: `${lessonPrice} ${currencyToSymbol[t.currency]}`,
      priceNumber: lessonPrice,
      rating: convertToPercentage(t.rating, 5),
      ratingCount: t.ratingCount,
      isFeatured: t.isFeatured,
      currency: currencyToSymbol[t.currency],
      available_slots: t.available_slots,
      lessonCount: t.lessonCount,
    };
  });

  response.feedback =
    feedback?.data?.map((f) => {
      return {
        id: f.id,
        author: f.byUserDisplayName,
        text: f.comment.slice(0, 220),
        rating: convertToPercentage(f.rating, 5),
        for: f.forUserFirstName,
        tag: f.forUserTag,
        subject: f.subject,
      };
    }) || [];

  return response;
};

const homePage = ({ pageData, subjectsData, subjectswithcounts }) => {
  const { t, i18n } = useTranslation();
  const [bestTeachers, setBestTeachers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  useEffect(() => {
    subjectsPresenter.updateSubjects(subjectsData);
    const controller = new AbortController();

    const load = async () => {
      const { bestTeachers, feedback } = await loadData(controller);
      setBestTeachers(bestTeachers);
      setFeedback(feedback);
    };
    load();
    return () => controller?.abort();
  }, []);

  const howItWorksSectionViewModel = getHowItWorksSectionViewModel(
    pageData?.sections
  );

  const pageDescription =
    pageData?.mainDescription?.childMarkdownRemark?.htmlAst;
  const educationExpertData = {
    title: pageData?.educationExpertTitle,
    firstButton: pageData?.educationExpertFirstButtonText,
    secondButton: pageData?.educationExpertSecondButtonText,
    firstFeatures: pageData?.educationExpertFirstButtonFeatures,
    secondFeatures: pageData?.educationExpertSecondButtonFeatures,
    contactForm: pageData?.educationExpertContactForm,
  };
  return (
    <>
      <HomeBanner subjects={subjectsData}>
        <PageBanner>
          <>
            <Themed.h1 sx={{ lineHeight: 1.2, marginBottom: 3 }}>
              {pageData?.mainTitle}
            </Themed.h1>

            {pageDescription && (
              <Box sx={{ fontSize: [2, 3] }}>
                {renderHtmlToReact(pageDescription)}
              </Box>
            )}

            <SubjectsDropdown
              placeholder={pageData?.mainSelectSubject}
              subjectsView={subjectswithcounts}
            />
          </>
        </PageBanner>
      </HomeBanner>

      <div className="relative">
        <Container px={4} pt={6} pb={4}>
          <Box
            sx={{
              maxWidth: '750px',
              position: 'relative',
              zIndex: '1',
            }}
          >
            <Themed.h2 sx={{ lineHeight: 1.2 }}>
              {pageData?.whyToChooseTitle}
            </Themed.h2>
            <Box sx={{ fontSize: [3, 4] }}>
              {pageData?.whyToChooseDescription?.childMarkdownRemark?.htmlAst &&
                renderHtmlToReact(
                  pageData?.whyToChooseDescription?.childMarkdownRemark?.htmlAst
                )}
            </Box>
          </Box>
          <Grid
            py={5}
            columns={[1, 2, 2, 4]}
            sx={{ alignSelf: 'center', position: 'relative' }}
            gap="60px"
          >
            {pageData?.whyToChooseFeatures?.map((item) => {
              return (
                <Box key={item.title}>
                  <img alt="" src={item.icon.file.url} height="50" width="50" />
                  <Themed.h3
                    sx={{
                      fontSize: [4, '28px'],
                      lineHeight: '1.2',
                    }}
                  >
                    {item.title}
                  </Themed.h3>
                  <Box sx={{ color: 'text700', fontSize: 3 }}>
                    {item?.description?.childMarkdownRemark?.htmlAst &&
                      renderHtmlToReact(
                        item.description.childMarkdownRemark.htmlAst
                      )}
                  </Box>
                </Box>
              );
            })}
          </Grid>

          <Box
            sx={{
              textAlign: 'center',
              position: 'relative',
              zIndex: '1',
            }}
          >
            <Link to={`/${PageSlugs.teachers[i18n.language]}`}>
              <Button sx={{ fontSize: [3, 4] }}>{t('findATeacher')}</Button>
            </Link>
          </Box>
        </Container>
      </div>
      {educationExpertData && (
        <div className="relative">
          <CirclesPurpleIcon className="circle-bottom-left circle-purple" />
          <EducationExpertSection sectionData={educationExpertData} />
        </div>
      )}

      <Box bg="greenPale" className="relative">
        <CirclesPurpleIcon className="circle-bottom-right circle-orange" />

        <Box
          sx={{
            color: '#e9f9ef',
            height: '90%',
            width: '100%',
            position: 'absolute',
            left: '50%',
            bottom: '15px',
            transform: 'translateX(-50%)',
          }}
        >
          <LogoIcon sx={{ width: '100%', height: '100%' }} />
        </Box>

        <Container px={4} py={6}>
          <Themed.h2
            sx={{
              marginTop: 0,
              textAlign: 'center',
              lineHeight: '1.2',
              position: 'relative',
              zIndex: '1',
            }}
          >
            {pageData?.subjectsListTitle}
          </Themed.h2>

          <Flex
            sx={{
              gap: '60px',
              paddingTop: 5,
              justifyContent: [
                'space-around',
                'space-around',
                'space-around',
                'space-between',
              ],
              flexWrap: 'wrap',
              position: 'relative',
            }}
          >
            {subjectswithcounts.map(({ slug, name, image, count }) => {
              return (
                <SubjectCard
                  key={slug}
                  slug={slug}
                  name={name}
                  image={image}
                  count={count}
                />
              );
            })}
          </Flex>
        </Container>
      </Box>

      {howItWorksSectionViewModel && (
        <HowThisWorkSection vm={howItWorksSectionViewModel} />
      )}

      {pageData?.reklaminiaiBlokai && (
        <AdsBlocksSection data={pageData?.reklaminiaiBlokai} />
      )}

      {pageData?.banner && (
        <CtaBanner
          variant="primary"
          title={pageData?.banner?.title}
          description={
            pageData?.banner?.description?.childMarkdownRemark?.htmlAst
          }
          buttonText={pageData?.banner?.buttonText}
          buttonLink={PageSlugs.registracija[i18n.language]}
          image={pageData?.banner?.image}
          svgElements
        />
      )}

      <TeacherSection
        title={pageData?.bestTeachersTitle}
        teachers={bestTeachers}
        linkToTeachersPage={`/${PageSlugs.teachers[i18n.language]}`}
      />
      {pageData?.dealsFromParntners && (
        <AdsBlocksSection data={pageData?.dealsFromParntners} />
      )}
      <FeedbackHome
        title={pageData?.whatStudentThinkTitle}
        feedback={feedback}
      />

      <CtaBanner
        variant="secondary"
        splitVerticalBgColor={feedback.length ? 'primaryPaleBg' : ''}
        title={pageData?.discountBanner.title}
        description={
          pageData?.discountBanner?.description?.childMarkdownRemark?.htmlAst
        }
        buttonText={pageData?.discountBanner.buttonText}
        buttonLink={pageData?.discountBanner.buttonLink}
        image={pageData?.discountBanner.image}
        svgElements
      />
    </>
  );
};

export default homePage;
